// ════════════════════════════════════════════════════════════════════════════
//  Contas a Receber — tela OPERACIONAL ("o que eu recebo hoje").
//
//  Paridade com o desktop: UMA LINHA POR PARCELA (contas_receber_analitica),
//  com vencimento, valor e saldo DA PARCELA. O título (contas_receber) é o
//  acordo que agrupa — sua visão consolidada fica no drawer de detalhe.
//
//  Duas operações distintas, como no desktop:
//    • Receber a PARCELA da linha (aceita valor parcial);
//    • Quitar o TÍTULO inteiro, no menu de ações da linha.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from "react";
import {
  Plus, Calendar, DollarSign, AlertCircle, CheckCircle,
  Clock, Search, Pencil, Trash2, CheckCheck, RotateCcw,
  ChevronLeft, ChevronRight, ListChecks, Eye, X, Wallet, Ban, Handshake,
} from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { useParcelasReceber } from "@/features/financeiro/hooks/useContasReceber";
import { ContaReceberForm } from "@/features/financeiro/components/ContaReceberForm";
import { ContaReceberEditDialog } from "@/features/financeiro/components/ContaReceberEditDialog";
import { DeleteConfirmDialog } from "@/features/financeiro/components/DeleteConfirmDialog";
import { BaixaParcelaDialog } from "@/features/financeiro/components/BaixaParcelaDialog";
import { QuitarTituloDialog } from "@/features/financeiro/components/QuitarTituloDialog";
import { EstornoDialog } from "@/features/financeiro/components/EstornoDialog";
import { ContaReceberDrawer } from "@/features/financeiro/components/ContaReceberDrawer";
import { ColunaOrdenavel as Col } from "@/features/financeiro/components/ColunaOrdenavel";
import { PeriodoFiltro, usePeriodo } from "@/components/listas/PeriodoFiltro";
import { calcularStatus } from "@/features/financeiro/types";
import type { StatusConta, ContaReceberParcelaDto, StatusFiltro,
  OrdenarPorReceber, DirecaoOrdenacao, BaseDataFinanceira,
  AtualizarContaReceberRequest } from "@/features/financeiro/types";
import type { ContaReceberFormData } from "@/features/financeiro/schemas/contaReceberSchema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePermissoes } from "@/contexts/PermissoesContext";
import { FORMULARIOS } from "@/domain/formularios";

const PAGE_SIZES = [20, 50, 100];

const statusColors: Record<StatusConta, string> = {
  pago:     "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  recebido: "bg-green-500/10 text-green-500 hover:bg-green-500/20",
  pendente: "bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20",
  atrasado: "bg-red-500/10 text-red-500 hover:bg-red-500/20",
};
const statusLabels: Record<StatusConta, string> = {
  pago: "Recebido", recebido: "Recebido", pendente: "Pendente", atrasado: "Atrasado",
};

// Título cancelado não é "atrasado": ele não é mais cobrável, e os KPIs já o
// excluem do que está em aberto. Sem este estado a linha aparecia em vermelho
// como se fosse dívida viva — grid e KPI contando coisas diferentes é
// exatamente a queixa que originou esta tela.
const CANCELADO = "bg-muted text-muted-foreground hover:bg-muted";

// Renegociado NÃO é "recebido" nem "cancelado": a dívida foi substituída por
// outro título. Rotular errado aqui faz o usuário procurar dinheiro que nunca
// entrou, ou dar por perdida uma dívida que continua viva no acordo.
const RENEGOCIADO = "bg-purple-500/10 text-purple-500 hover:bg-purple-500/20";

const ContasReceber = () => {
  const { temPermissao } = usePermissoes();
  const podeIncluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "incluir");
  const podeEditar  = temPermissao(FORMULARIOS.CONTAS_RECEBER, "editar");
  const podeExcluir = temPermissao(FORMULARIOS.CONTAS_RECEBER, "excluir");

  const [pagina, setPagina]               = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [searchTerm, setSearchTerm]       = useState("");
  const [statusFilter, setStatusFilter]   = useState<StatusFiltro>("todos");
  // Período sobre a data escolhida em "Data por". Abre no mês corrente INTEIRO
  // (até o último dia): o que vence ainda este mês é o que a tela precisa mostrar.
  const periodo = usePeriodo("mes", undefined, { mesInteiro: true });
  const { dtInicio, dtFim } = periodo.intervalo;
  // Base do período. Vencimento é o padrão — é a pergunta da tela operacional.
  const [baseData, setBaseData]           = useState<BaseDataFinanceira>("vencimento");
  const [open, setOpen]                   = useState(false);
  const [selectedIds, setSelectedIds]     = useState<number[]>([]);
  const [detalheId, setDetalheId]         = useState<number | null>(null);

  // Alvos das ações — separados porque parcela e título são coisas diferentes.
  const [baixando, setBaixando]               = useState<ContaReceberParcelaDto | null>(null);
  const [estornoParcelaId, setEstornoParcelaId] = useState<number | null>(null);
  const [excluindo, setExcluindo]             = useState<ContaReceberParcelaDto | null>(null);
  const [quitandoTitulo, setQuitandoTitulo]   = useState<ContaReceberParcelaDto | null>(null);
  const [editandoTituloId, setEditandoTituloId] = useState<number | null>(null);

  // Padrão: vencimento CRESCENTE — o que vence (ou já venceu) primeiro no topo,
  // que é a ordem de cobrança. Antes era pelo Id (ordem de cadastro).
  const [ordenarPor, setOrdenarPor] = useState<OrdenarPorReceber>("vencimento");
  const [direcao, setDirecao]       = useState<DirecaoOrdenacao>("asc");

  const hoje = new Date().toISOString().slice(0, 10);
  const buscaAplicada = useDebounce(searchTerm, 400);

  useEffect(() => {
    setPagina(1);
    setSelectedIds([]);
  }, [buscaAplicada, statusFilter, dtInicio, dtFim, baseData, tamanhoPagina, ordenarPor, direcao]);

  function ordenarPorCampo(campo: OrdenarPorReceber) {
    if (campo === ordenarPor) setDirecao((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrdenarPor(campo); setDirecao("desc"); }
  }
  const ordProps = { ordenarPor, direcao, onOrdenar: ordenarPorCampo };

  const {
    parcelas, total, totalPaginas, totais, isLoading,
    criarConta, isCriando,
    excluirTitulo, excluirParcela,
    baixarParcela, isBaixandoParcela,
    baixarParcelasLote, isBaixandoLote,
    estornarParcela, isEstornandoParcela,
    quitarTitulo, isQuitandoTitulo,
    atualizarConta, isAtualizando,
  } = useParcelasReceber({
    pagina, tamanhoPagina,
    busca: buscaAplicada,
    status: statusFilter,
    dtInicio, dtFim,
    baseData,
    incluirVencidos: true,
    ordenarPor, direcao,
  });

  const temFiltro = !!buscaAplicada || statusFilter !== "todos" || periodo.periodo !== "mes"
    || baseData !== "vencimento";
  function limparFiltros() {
    setSearchTerm(""); setStatusFilter("todos"); periodo.setPeriodo("mes");
    setBaseData("vencimento");
  }

  // ── KPIs (do conjunto filtrado inteiro, por parcela, vindos do servidor) ──
  const totalReceber  = totais.totalEmAberto;
  const totalRecebido = totais.totalQuitado;
  const totalAtrasado = totais.totalAtrasado;

  // ── Seleção múltipla: só parcelas REAIS e em aberto ──────────────────────
  // Título órfão (semParcela) tem id 0 — não há parcela para o lote baixar.
  const selecionaveis = parcelas.filter((p) => !p.isPago && !p.semParcela && p.id > 0).map((p) => p.id);
  const allSelected   = selecionaveis.length > 0 && selecionaveis.every((id) => selectedIds.includes(id));
  const someSelected  = selecionaveis.some((id) => selectedIds.includes(id));

  function toggleAll() { setSelectedIds(allSelected ? [] : selecionaveis); }
  function toggleOne(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleCreate(data: ContaReceberFormData) {
    criarConta(
      {
        clienteId:    data.clienteId,
        operacaoId:   1,
        lancamentoId: 1,
        dtEmissao:    data.dtEmissao || hoje,
        dtVencimento: data.dtVencimento,
        valorParcela: data.valorParcela,
        qtdParcelas:  data.qtdParcelas,
        diasParcelas: data.diasParcelas || undefined,
        diaFixo:      data.diaFixo,
        numDocumento: data.numDocumento,
        lancamento:   data.lancamento,
      },
      { onSuccess: () => setOpen(false) }
    );
  }

  // O diálogo já monta o request (decide se envia parcelamento conforme haja
  // recebimento) — aqui só repassamos.
  function handleUpdate(id: number, data: AtualizarContaReceberRequest) {
    atualizarConta(
      { id, data },
      { onSuccess: () => setEditandoTituloId(null) }
    );
  }

  function handleBaixarLote() {
    baixarParcelasLote(
      { ids: selectedIds, dtPagamento: hoje },
      { onSuccess: () => setSelectedIds([]) }
    );
  }

  const brl = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Contas a Receber</h1>
          <p className="text-muted-foreground mt-1">Uma linha por parcela — acompanhe seus recebimentos</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="secondary" className="gap-2"
              onClick={handleBaixarLote} disabled={isBaixandoLote}
            >
              <ListChecks className="h-4 w-4" />
              Receber Selecionadas ({selectedIds.length})
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            {podeIncluir && (
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" /> Nova Conta
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nova Conta a Receber</DialogTitle>
                <DialogDescription>
                  Informe o valor de UMA parcela e quantas parcelas — o título será
                  o valor × a quantidade. Campos com * são obrigatórios.
                </DialogDescription>
              </DialogHeader>
              <ContaReceberForm
                onSubmit={handleCreate}
                onCancel={() => setOpen(false)}
                isLoading={isCriando}
                defaultValues={{ dtEmissao: hoje, qtdParcelas: 1, diaFixo: false }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Total a Receber
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{brl(totalReceber)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totais.qtdEmAberto} parcela(s) em aberto
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Total Recebido
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{brl(totalRecebido)}</div>
            <p className="text-xs text-muted-foreground mt-1">{totais.qtdQuitado} parcela(s)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Atrasado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{brl(totalAtrasado)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totais.qtdAtrasado} parcela(s)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Próximo Recebimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {totais.proximoVencimento ? (
              <>
                <div className="text-2xl font-bold">
                  {new Date(totais.proximoVencimento).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {brl(totais.proximoVencimentoValor ?? 0)}
                </p>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Nenhuma parcela pendente</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tabela ── */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle>Parcelas a Receber</CardTitle>
              <span className="text-sm text-muted-foreground">({total} total)</span>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full md:w-auto items-end">
              <div className="relative flex-1 md:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por cliente, nº documento ou duplicata..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground leading-none mb-1">Data por</span>
                <Select value={baseData} onValueChange={(v) => setBaseData(v as BaseDataFinanceira)}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vencimento">Vencimento</SelectItem>
                    <SelectItem value="emissao">Emissão</SelectItem>
                    <SelectItem value="pagamento">Pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground leading-none mb-1">Período</span>
                <div className="flex flex-col sm:flex-row gap-2">
                  <PeriodoFiltro estado={periodo}
                    rotuloData={baseData === "emissao" ? "Emissão" : baseData === "pagamento" ? "Pagamento" : "Vencimento"} />
                </div>
                {baseData === "vencimento" && periodo.periodo !== "todos" && (
                  <span className="text-[10px] text-muted-foreground mt-1">+ vencidos em aberto de antes do período</span>
                )}
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFiltro)}>
                <SelectTrigger className="w-full sm:w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Recebido</SelectItem>
                  <SelectItem value="atrasado">Atrasado</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(tamanhoPagina)} onValueChange={(v) => setTamanhoPagina(Number(v))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {temFiltro && (
                <Button variant="ghost" size="sm" onClick={limparFiltros} title="Limpar filtros" className="gap-1">
                  <X className="h-4 w-4" /> Limpar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Selecionar todas as parcelas em aberto"
                      data-state={someSelected && !allSelected ? "indeterminate" : undefined}
                    />
                  </TableHead>
                  <Col campo="cliente"      {...ordProps}>Cliente</Col>
                  <Col campo="documento"    {...ordProps}>Nº Documento</Col>
                  <Col campo="parcela"      {...ordProps}>Parcela</Col>
                  <Col campo="vencimento"   {...ordProps}>Vencimento</Col>
                  <Col campo="valorReceber" {...ordProps} alinharDireita>Valor Parcela</Col>
                  <Col campo="saldo"        {...ordProps} alinharDireita>Saldo</Col>
                  <Col campo="status"       {...ordProps}>Status</Col>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : parcelas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhuma parcela encontrada</TableCell>
                  </TableRow>
                ) : parcelas.map((p) => {
                  const status  = calcularStatus(p.isPago, p.dtVencimento);
                  const checked = p.id > 0 && selectedIds.includes(p.id);
                  // Título cancelado: a API recusa a baixa (TITULO_CANCELADO), então
                  // a tela não oferece a ação em vez de deixar o usuário levar erro.
                  const podeOperarParcela =
                    !p.semParcela && p.id > 0 && !p.tituloIsCancelado;
                  return (
                    <TableRow key={`${p.tituloId}-${p.id}`} data-selected={checked || undefined}>
                      <TableCell>
                        {!p.isPago && podeOperarParcela && (
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleOne(p.id)}
                            aria-label={`Selecionar parcela ${p.nroParcela} do título ${p.tituloId}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium" title={`Cliente #${p.clienteId}`}>
                        {p.clienteNome || `Cliente #${p.clienteId}`}
                      </TableCell>
                      <TableCell>{p.numDocumento || p.duplicata || "-"}</TableCell>
                      <TableCell>
                        {p.nroParcela}/{p.qtdParcelas}
                        {p.semParcela && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px]"
                            title="Título legado sem parcela registrada — a linha vem do cabeçalho. Use 'Quitar título' para receber."
                          >
                            sem parcela
                          </Badge>
                        )}
                        {/* A dívida continua aqui, mas a cobrança está agrupada
                            numa fatura — avisar evita cobrar duas vezes. */}
                        {p.isFaturada && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-[10px] border-blue-500/40 text-blue-500"
                            title={`Parcela agrupada na fatura #${p.faturaId} — a cobrança é feita por ela`}
                          >
                            fatura #{p.faturaId}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{new Date(p.dtVencimento).toLocaleDateString("pt-BR")}</TableCell>
                      <TableCell className="text-right">{brl(p.valorReceber)}</TableCell>
                      <TableCell className="text-right font-medium">{brl(p.saldo)}</TableCell>
                      <TableCell>
                        {/* Renegociado vem ANTES de cancelado: a renegociação
                            cancela o título, mas "Cancelado" esconderia que a
                            dívida virou outro título em vez de sumir. */}
                        {p.isRenegociada ? (
                          <Badge
                            className={RENEGOCIADO}
                            title={`Dívida substituída pelo título #${p.renegociadaEmTituloId} — não foi recebida`}
                          >
                            <span className="flex items-center gap-1">
                              <Handshake className="h-3 w-3" /> Renegociado
                            </span>
                          </Badge>
                        ) : p.tituloIsCancelado ? (
                          <Badge className={CANCELADO} title="Título cancelado — não entra nos totais a receber">
                            <span className="flex items-center gap-1">
                              <Ban className="h-3 w-3" /> Cancelado
                            </span>
                          </Badge>
                        ) : (
                          <Badge className={statusColors[status]}>
                            <span className="flex items-center gap-1">
                              {status === "pago" || status === "recebido"
                                ? <CheckCircle className="h-3 w-3" />
                                : status === "atrasado"
                                ? <AlertCircle className="h-3 w-3" />
                                : <Clock className="h-3 w-3" />}
                              {statusLabels[status]}
                            </span>
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => setDetalheId(p.tituloId)}
                            title="Ver o título completo"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {/* Editar o TÍTULO. Aparece mesmo com recebimento: o
                              diálogo trava o parcelamento e libera a classificação
                              (conta contábil / tipo de despesas / documento), que é
                              o que o usuário precisa corrigir depois que o dinheiro
                              entrou. Só esconde em cancelado/renegociado. */}
                          {podeEditar && !p.tituloIsCancelado && !p.isRenegociada && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setEditandoTituloId(p.tituloId)}
                              title="Editar o título"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Receber ESTA parcela (aceita valor parcial). */}
                          {!p.isPago && podeOperarParcela && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setBaixando(p)}
                              disabled={isBaixandoParcela}
                              title="Receber esta parcela"
                            >
                              <CheckCheck className="h-4 w-4" />
                            </Button>
                          )}

                          {/* Quitar o TÍTULO inteiro — operação distinta. */}
                          {!p.tituloIsPago && !p.tituloIsCancelado && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setQuitandoTitulo(p)}
                              disabled={isQuitandoTitulo}
                              title={`Quitar o título inteiro (saldo ${brl(p.tituloSaldo)})`}
                            >
                              <Wallet className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}

                          {/* Estorno vale também para a parcela PARCIALMENTE recebida
                              (valorPago > 0 e ainda aberta) — senão um recebimento
                              parcial digitado errado ficaria sem como desfazer. */}
                          {(p.isPago || p.valorPago > 0) && podeOperarParcela && podeEditar && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setEstornoParcelaId(p.id)}
                              disabled={isEstornandoParcela}
                              title={p.isPago
                                ? "Estornar o recebimento desta parcela"
                                : `Estornar o recebimento parcial (${brl(p.valorPago)})`}
                            >
                              <RotateCcw className="h-4 w-4 text-orange-500" />
                            </Button>
                          )}

                          {/* Excluir só enquanto NÃO há recebimento algum — a API
                              recusa (PARCELA_COM_BAIXA) e deixaria o caixa órfão. */}
                          {podeExcluir && !p.isPago && p.valorPago <= 0 && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setExcluindo(p)}
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {totalPaginas > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Página {pagina} de {totalPaginas} ({total} parcelas)
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setPagina((p) => p - 1)} disabled={pagina <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Dialogs ── */}
      <BaixaParcelaDialog
        parcela={baixando}
        onOpenChange={(o) => !o && setBaixando(null)}
        onConfirm={(parcelaId, req) =>
          baixarParcela({ parcelaId, req }, { onSuccess: () => setBaixando(null) })}
        isLoading={isBaixandoParcela}
      />

      {/* Quitação do TÍTULO: fecha TODAS as parcelas de uma vez, com data e forma. */}
      <QuitarTituloDialog
        open={!!quitandoTitulo}
        onOpenChange={(o) => !o && setQuitandoTitulo(null)}
        tipo="receber"
        pessoaNome={quitandoTitulo?.clienteNome}
        saldo={quitandoTitulo?.tituloSaldo ?? 0}
        isLoading={isQuitandoTitulo}
        onConfirm={(req) => {
          if (quitandoTitulo) {
            quitarTitulo(
              { id: quitandoTitulo.tituloId,
                req: { dtPagamento: req.dtPagamento, valorPago: quitandoTitulo.tituloSaldo,
                       formaPagamentoId: req.formaPagamentoId } },
              { onSuccess: () => setQuitandoTitulo(null) },
            );
          }
        }}
      />

      <EstornoDialog
        open={!!estornoParcelaId}
        onOpenChange={(o) => !o && setEstornoParcelaId(null)}
        tipo="receber"
        onConfirm={() => {
          if (estornoParcelaId)
            estornarParcela(estornoParcelaId, { onSuccess: () => setEstornoParcelaId(null) });
        }}
        isLoading={isEstornandoParcela}
      />

      {/* Excluir: a parcela quando ela existe; o título quando é a única/órfã. */}
      <DeleteConfirmDialog
        open={!!excluindo}
        onOpenChange={(o) => !o && setExcluindo(null)}
        onConfirm={() => {
          if (!excluindo) return;
          if (excluindo.semParcela || excluindo.qtdParcelas <= 1) excluirTitulo(excluindo.tituloId);
          else excluirParcela(excluindo.id);
          setExcluindo(null);
        }}
        title={
          excluindo && !excluindo.semParcela && excluindo.qtdParcelas > 1
            ? "Excluir parcela"
            : "Excluir conta a receber"
        }
        description={
          excluindo && !excluindo.semParcela && excluindo.qtdParcelas > 1
            ? `A parcela ${excluindo.nroParcela}/${excluindo.qtdParcelas} será removida e as ` +
              `demais renumeradas. Esta ação não pode ser desfeita.`
            : "O título e suas parcelas serão excluídos. Esta ação não pode ser desfeita."
        }
      />

      <ContaReceberEditDialog
        tituloId={editandoTituloId}
        onOpenChange={(o) => !o && setEditandoTituloId(null)}
        onConfirm={handleUpdate}
        isLoading={isAtualizando}
      />

      <ContaReceberDrawer id={detalheId} onClose={() => setDetalheId(null)} />
    </div>
  );
};

export default ContasReceber;
